import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/api";
import { trackEvent } from "@/analytics";
import PageHero from "@/components/tex/PageHero";

export default function ContactPage() {
  const { company, openQuote } = useOutletContext();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");
  const [reference, setReference] = useState("");

  useEffect(() => {
    document.title = "Contact — Texcheme";
    window.scrollTo(0, 0);
  }, []);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function submit(e) {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await api.submitContact(form);
      setReference(res.reference || "");
      setStatus("sent");
      trackEvent("contact_submitted", { subject: form.subject, reference: res.reference });
    } catch (err) {
      setErrorMsg(err.message || "Something went wrong — please try again.");
      setStatus("error");
    }
  }

  const addr = company?.address || {};

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Talk to us about your supply"
        lead="Quotes, standing orders, COA and grade questions, or anything else — reach the team directly."
      />

      <section className="panel" data-testid="contact-body">
        <div className="wrap contact-grid">
          <div className="contact-info">
            <div className="panel-head" style={{ marginBottom: 20 }}>
              <div className="eyebrow">Reach us</div>
              <h2>Company details</h2>
            </div>

            <div className="contact-block">
              <span className="card-label">Email</span>
              <p>{company?.quote_email || "shiva@texcheme.com"}</p>
            </div>
            <div className="contact-block">
              <span className="card-label">Phone</span>
              <p>{company?.phone || "+91-74188 55555"}</p>
            </div>
            <div className="contact-block">
              <span className="card-label">Address</span>
              <p>
                {addr.line1}
                {addr.line1 && <br />}
                {addr.line2}
                {addr.line2 && <br />}
                {addr.country}
              </p>
            </div>
            <div className="contact-block">
              <span className="card-label">Hours</span>
              <p>{company?.hours}</p>
            </div>

            <div className="contact-quote-nudge">
              <p>Ready to price a bulk order?</p>
              <button className="btn btn-brand btn-sm" onClick={() => openQuote("contact-page")}>
                Request a quote instead
              </button>
            </div>
            <p className="note">{company?.note}</p>
          </div>

          <div className="contact-form-wrap">
            {status === "sent" ? (
              <div className="card contact-success" data-testid="contact-success">
                <h3>Message sent</h3>
                <p>
                  Thanks — we&apos;ve received your message{reference ? ` (ref ${reference})` : ""} and
                  will get back to you, usually the same working day.
                </p>
                <button
                  className="btn btn-brand"
                  onClick={() => {
                    setForm({ name: "", email: "", company: "", phone: "", subject: "", message: "" });
                    setStatus("idle");
                  }}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form className="card contact-form" onSubmit={submit} data-testid="contact-form">
                <div className="panel-head" style={{ marginBottom: 18 }}>
                  <div className="eyebrow">Send a message</div>
                  <h2 style={{ fontSize: 22 }}>How can we help?</h2>
                </div>

                <div className="form-row">
                  <label>
                    Name
                    <input required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="contact-name" />
                  </label>
                  <label>
                    Work email
                    <input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="contact-email" />
                  </label>
                </div>

                <div className="form-row">
                  <label>
                    Company (optional)
                    <input value={form.company} onChange={(e) => update("company", e.target.value)} data-testid="contact-company" />
                  </label>
                  <label>
                    Phone (optional)
                    <input value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="contact-phone" />
                  </label>
                </div>

                <label>
                  Subject
                  <input required value={form.subject} onChange={(e) => update("subject", e.target.value)} placeholder="e.g. HCl 33% supply for a pickling line" data-testid="contact-subject" />
                </label>

                <label>
                  Message
                  <textarea required rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} data-testid="contact-message" />
                </label>

                {status === "error" && <p className="modal-error" data-testid="contact-error">{errorMsg}</p>}

                <button className="btn btn-brand" type="submit" disabled={status === "sending"} data-testid="contact-submit">
                  {status === "sending" ? "Sending…" : "Send message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
